/**
 * upload/InstructionsStage.tsx — stage 2, and the heart of this feature.
 *
 * WILL'S REQUIREMENT, IMPLEMENTED LITERALLY: "User has to go through each component of the flow,
 * explaining how to set it up." One required explanation field is generated for EVERY component the
 * backend found in THIS automation's definition — trigger, actions, conditions, each branch, loops,
 * scopes, switch cases and every nested action. Nothing about the number or shape of the sections is
 * hardcoded: a two-step flow renders two sections and a fifty-step flow renders fifty, from the same
 * code, because the list comes from `inspect-components`.
 *
 * WHAT THIS FILE DOES NOT DO — deliberately, so the gate cannot be subverted by presentation:
 *   - It does not decide what "explained" means. `isExplained` from @trailblazer/shared does, and the
 *     backend uses the same predicate on submit.
 *   - It does not validate only what is expanded. Completion is computed over the WHOLE component
 *     list, so a collapsed section with an empty field still blocks progression and still shows as
 *     outstanding on its own collapsed header.
 *   - It does not own the Next button. The overlay does, and it re-checks completion itself.
 *
 * HIERARCHY IS SHOWN THREE WAYS so it survives a narrow screen, greyscale and a screen reader:
 * hierarchical numbering (3.2.1), left indentation by depth, and an explicit branch chip ("If yes").
 * Indentation stops growing past depth 4 so a deeply nested flow never scrolls the modal sideways.
 */
import {
  isExplained,
  MAX_EXPLANATION_LENGTH,
  type ExplanationMap,
  type FlowComponent,
} from '@trailblazer/shared';

export interface InstructionsStageProps {
  components: FlowComponent[];
  explanations: ExplanationMap;
  /** Ids the builder must still explain. Shown inline; the overlay computes and gates on it. */
  missingIds: string[];
  /** Only true once Next has been pressed, so the stage does not flag untouched fields up front. */
  showErrors: boolean;
  /** Collapsed component ids. Collapsing is presentation only — it never affects validation. */
  collapsedIds: Set<string>;
  onToggleCollapse: (id: string) => void;
  onChange: (componentId: string, explanation: string) => void;
  /** Registers a textarea so the overlay can focus the first unexplained one. */
  registerField: (componentId: string, el: HTMLTextAreaElement | null) => void;
  /** Set when a restored draft referred to components this flow no longer has. */
  droppedCount?: number;
}

/**
 * The read-only context line under a component's name: what kind of step it is, and which connector
 * and operation it uses. Enough for the builder to recognise the step in the Power Automate designer,
 * with no raw JSON — the task is explicit that an intimidating JSON dump is not acceptable, and the
 * backend never sends `inputs` anyway.
 */
function componentMeta(component: FlowComponent): string {
  const parts: string[] = [];
  if (component.typeLabel) parts.push(component.typeLabel);
  if (component.connector) parts.push(component.connector);
  if (component.operation) parts.push(component.operation);
  return parts.join(' · ');
}

/** The prompt text, tuned to what the component actually is — a trigger and a loop need different asks. */
function promptFor(component: FlowComponent): string {
  if (component.kind === 'trigger') {
    return 'What starts this flow, and what must be in place first? Name the form, list, mailbox or schedule it watches.';
  }
  if (component.actionType === 'If') {
    return 'What is this condition checking, and what decides yes or no? Say which value an adopter may need to change.';
  }
  if (component.actionType === 'Foreach' || component.actionType === 'Until') {
    return 'What does this loop run over, and how many items should an adopter expect?';
  }
  if (component.actionType === 'Switch') {
    return 'What value is being matched here, and what should an adopter change for their own cases?';
  }
  if (component.kind === 'control') {
    return 'What is this group of steps for, and does anything about it need configuring?';
  }
  return 'What does this step do, what needs configuring, and which connection or account is required?';
}

export function InstructionsStage(props: InstructionsStageProps) {
  const { components, explanations, missingIds, showErrors, collapsedIds } = props;
  const missing = new Set(missingIds);

  return (
    <div>
      <p style={{ marginTop: 0 }}>
        These are the actual steps of your automation, in the order they run. Explain each one so a
        colleague can set it up themselves — <strong>every step needs an explanation</strong> before
        you can continue.
      </p>

      {props.droppedCount ? (
        <div className="tb-note tb-note--warn" role="status">
          Your flow has changed since you saved this draft. {props.droppedCount}{' '}
          {props.droppedCount === 1 ? 'explanation' : 'explanations'} referred to steps that are no
          longer in it, so {props.droppedCount === 1 ? 'it has' : 'they have'} been removed. Your
          other answers are intact.
        </div>
      ) : null}

      {showErrors && missingIds.length > 0 ? (
        <div className="tb-note tb-note--warn" role="alert">
          {missingIds.length === 1
            ? 'One step still needs an explanation.'
            : `${missingIds.length} steps still need an explanation.`}{' '}
          They are marked below.
        </div>
      ) : null}

      <ul className="tb-comp-list">
        {components.map((component) => {
          const value = explanations[component.id] ?? '';
          const isMissing = !isExplained(value);
          // Flag a specific field only after a Next attempt — but the header state marker is always
          // accurate, so progress is visible from the first keystroke without pre-emptive red.
          const flagged = showErrors && missing.has(component.id);
          const collapsed = collapsedIds.has(component.id);
          const bodyId = `tb-comp-body-${component.order}`;
          const errorId = `tb-comp-err-${component.order}`;
          const meta = componentMeta(component);

          return (
            <li
              key={component.id}
              className="tb-comp"
              data-depth={Math.min(component.depth, 5)}
              data-deep={component.depth > 5 ? 'true' : 'false'}
              data-missing={isMissing ? 'true' : 'false'}
            >
              <button
                type="button"
                className="tb-comp-head"
                aria-expanded={!collapsed}
                aria-controls={bodyId}
                onClick={() => props.onToggleCollapse(component.id)}
              >
                <span className="tb-comp-num">{component.numbering}</span>
                {component.branch ? (
                  <span className="tb-comp-branch">{component.branch}</span>
                ) : null}
                <span className="tb-comp-label">{component.label}</span>
                {/* Glyph + words: never colour alone (PC-NFR01). Visible even while collapsed. */}
                <span className="tb-comp-state" data-missing={isMissing ? 'true' : 'false'}>
                  {isMissing ? '● Needs explaining' : '✓ Explained'}
                </span>
                {meta ? <span className="tb-comp-meta">{meta}</span> : null}
              </button>

              {/* Kept mounted while collapsed (hidden), so its value is never lost by toggling. */}
              <div className="tb-comp-body" id={bodyId} hidden={collapsed}>
                <label className="tb-field">
                  <span className="tb-field-hint" style={{ fontWeight: 400 }}>
                    {promptFor(component)}
                  </span>
                  <textarea
                    value={value}
                    maxLength={MAX_EXPLANATION_LENGTH}
                    onChange={(e) => props.onChange(component.id, e.target.value)}
                    ref={(el) => props.registerField(component.id, el)}
                    aria-invalid={flagged ? true : undefined}
                    aria-describedby={flagged ? errorId : undefined}
                    aria-label={`How to set up step ${component.numbering}, ${component.label}`}
                  />
                  {flagged ? (
                    <span className="tb-err" id={errorId}>
                      Explain this step before continuing.
                    </span>
                  ) : null}
                </label>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
